import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useFetch } from "@/lib/hooks";
import { useSeo, usePageSeo, abs, SITE } from "@/lib/seo";
import { BLOG_CATEGORIES } from "@/lib/api";
import { BlogCard } from "@/components/site/Sections";
import { PhotoImg } from "@/components/site/PhotoImg";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { PageHero } from "./Gallery";

export function Blog() {
  const { t } = useLang();
  usePageSeo("blog");
  const [cat, setCat] = useState("");
  const { data } = useFetch(`/content/posts${cat ? `?category=${cat}` : ""}`);
  const posts = Array.isArray(data) ? data : [];
  return (
    <div data-testid="blog-page">
      <PageHero testid="blog-hero" kicker="Journal" title={t("blog")} lead={t("blogLead")} photo={posts[0]?.cover_photo} />
      <section className="mx-auto max-w-[1600px] px-5 py-16 md:px-10 md:py-24">
        <div className="no-scrollbar mb-12 flex gap-2 overflow-x-auto">
          {["", ...BLOG_CATEGORIES].map((c) => (
            <button key={c || "all"} onClick={() => setCat(c)} data-testid={`blog-filter-${c || "all"}`}
              className={`shrink-0 rounded-full border px-4 py-2 text-xs transition-colors ${cat === c ? "border-white bg-white text-black" : "border-white/20 text-white/75 hover:border-white/60"}`}>{c ? t(c) : t("allCategories")}</button>
          ))}
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">{posts.map((p, i) => <BlogCard key={p.id} post={p} i={i % 3} />)}</div>
      </section>
    </div>
  );
}

const Body = ({ text = "" }) => (
  <div className="prose-mroue" data-testid="blog-post-body">
    {text.split(/\n\s*\n/).map((b, i) => (b.trim().startsWith(">") ? <blockquote key={i}>{b.replace(/^>\s?/gm, "")}</blockquote> : <p key={i}>{b}</p>))}
  </div>
);

export function BlogPost() {
  const { slug } = useParams();
  const { t, L, lang } = useLang();
  const { data, error } = useFetch(`/content/posts/${slug}`);
  const cover = abs(data?.cover_photo?.urls?.medium);
  useSeo(data ? L(data, "title") : null, data ? L(data, "excerpt") : undefined, cover, data && {
    "@type": "BlogPosting", headline: L(data, "title"), description: L(data, "excerpt"), inLanguage: lang,
    url: `${SITE}/blog/${data.slug}`, mainEntityOfPage: `${SITE}/blog/${data.slug}`,
    ...(cover ? { image: cover } : {}), ...(data.published_at ? { datePublished: data.published_at } : {}),
    author: { "@id": `${SITE}/#photographer` }, publisher: { "@id": `${SITE}/#photographer` },
  });
  if (error) return <div className="px-5 pt-40 text-center text-white/70">404 · <Link to="/blog" className="underline">{t("backJournal")}</Link></div>;
  if (!data) return <div className="min-h-screen" />;
  return (
    <article data-testid="blog-post-page">
      <header className="relative flex min-h-[80vh] items-end overflow-hidden pb-16 pt-32">
        {data.cover_photo && <PhotoImg photo={data.cover_photo} eager sizes="100vw" className="!absolute inset-0 h-full w-full" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0b] via-[#0b0b0b]/50 to-black/30" />
        <div className="relative mx-auto w-full max-w-4xl px-5">
          <Link to="/blog" data-testid="blog-back-link" className="kicker mb-8 inline-flex items-center gap-2 !text-white/80 hover:!text-white"><ArrowLeft size={12} /> {t("backJournal")}</Link>
          <div className="kicker mb-5">{t(data.category)} · {new Date(data.published_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "2-digit", month: "long", year: "numeric" })} · {data.reading_minutes || 5} {t("minRead")}</div>
          <MaskLines as="h1" lines={[L(data, "title")]} className="font-display text-4xl font-extrabold leading-[0.95] text-white sm:text-6xl" testid="blog-post-title" />
        </div>
      </header>
      <Reveal className="mx-auto max-w-3xl px-5 pb-28 pt-8">
        <p className="mb-10 text-xl leading-relaxed text-white/85">{L(data, "excerpt")}</p>
        <Body text={L(data, "body")} />
      </Reveal>
    </article>
  );
}
